import { useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../services/api'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [retryAfter, setRetryAfter] = useState(0)
  const [loading, setLoading] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setRetryAfter(0)
    setLoading(true)
    try {
      await api.post('/api/auth/forgot-password', { email })
      setSent(true)
    } catch (err) {
      const status = err.response?.status
      const retryHeader = err.response?.headers?.['retry-after']
      // Rate limit — clear, user-friendly message with Retry-After
      // instead of a mysterious 429 (adversarial-review finding #24).
      if (status === 429) {
        const seconds = parseInt(retryHeader, 10) || 900
        setRetryAfter(seconds)
        setError(`You've hit the rate limit — please wait ${Math.ceil(seconds / 60)} minute${seconds >= 120 ? 's' : ''} and try again from this network.`)
      } else if (status === 503) {
        // Infra-wide failure (DB down, etc.).  Not per-account so it
        // doesn't leak enumeration.  Advertise the temporary nature.
        setError(err.response?.data?.error || 'Our servers are temporarily unavailable. Please try again in a few minutes.')
      } else if (status === 400 && err.response?.data?.error?.includes?.('Email delivery')) {
        // Fresh-install "SMTP not configured" surface — steer to admin
        // instead of leaving the user waiting for an email that will
        // never arrive.
        setError(err.response.data.error)
      } else {
        // Backend returns uniform success even for unknown emails, so an error
        // here means a server/network problem, not "email not found".
        setError(err.response?.data?.error || 'Could not submit request. Try again in a moment.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-600">Omnia Practice Management</h1>
          <p className="text-gray-500 mt-2">Forgot Password</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 rounded-lg p-3 mb-6 text-sm" role="alert">
            {error}
            {retryAfter > 0 && (
              <div className="mt-1 text-xs text-red-500">
                Retry after: {Math.ceil(retryAfter / 60)} minute{retryAfter >= 120 ? 's' : ''}.
              </div>
            )}
          </div>
        )}

        {sent ? (
          <div className="text-center">
            <div className="bg-green-50 text-green-700 rounded-lg p-4 mb-4 text-sm text-left">
              <div className="font-medium mb-1">If that email is registered, we've sent a reset link.</div>
              <div>Check your inbox (and spam folder). The link is valid for 24 hours.</div>
            </div>
            <div className="bg-blue-50 text-blue-800 rounded-lg p-3 mb-4 text-xs text-left">
              <strong>Have two-factor auth on your account?</strong> Resetting your password
              won't clear your 2FA enrollment — you'll still need your authenticator
              app to complete sign-in. Contact your administrator if you've also lost
              your authenticator.
            </div>
            <Link to="/login" className="text-primary-600 hover:underline text-sm font-medium">
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-gray-600">
              Enter your work email and we'll send you a link to reset your password.
              The link is valid for 24 hours.
            </p>
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="forgot-email" name="email" type="email" autoComplete="username"
                required autoFocus
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5
                           focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="you@lawfirm.com"
              />
            </div>
            <button
              type="submit" disabled={loading || !email}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white
                         font-semibold rounded-lg py-2.5 transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-gray-500 hover:underline">
                ← Back to sign in
              </Link>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Omnia Practice Management © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
